import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { RevealOnScroll } from "@/components/site/Reveal";
import { motion } from "framer-motion";

/**
 * Configurable request/enquiry form. `kind` decides:
 *  - the endpoint hit
 *  - the extra fields shown
 *  - copy
 */
const CONFIG = {
  quote: {
    endpoint: "/quotes",
    overline: "Request a Quotation",
    heading: ["Numbers,", "on <em>request</em>."],
    intro: "Tell us what interests you. We'll respond within one business day with a formal quotation — inclusive of shipping to Ghana.",
    extra: [
      { name: "vehicle_slug", label: "Vehicle (optional)", type: "text", placeholder: "e.g. Jetour T2 PHEV" },
      { name: "budget", label: "Budget bracket (GHS)", type: "text", placeholder: "e.g. 200,000 – 300,000" },
    ],
  },
  reserve: {
    endpoint: "/reservations",
    overline: "Reserve a Vehicle",
    heading: ["Hold it,", "before it's <em>gone</em>."],
    intro: "A refundable reservation holds the vehicle exclusively for you until inspection.",
    extra: [
      { name: "vehicle_slug", label: "Vehicle", type: "text", required: true, placeholder: "e.g. changan-uni-t-2020" },
      { name: "deposit_ready", label: "Deposit ready", type: "checkbox" },
    ],
  },
  finance: {
    endpoint: "/finance-enquiries",
    overline: "Finance Enquiry",
    heading: ["Financing,", "on the <em>way</em>."],
    intro: "Financing partnerships arriving soon. Register your interest and we'll be in touch the moment terms are ready.",
    extra: [
      { name: "vehicle_slug", label: "Vehicle (optional)", type: "text" },
      { name: "down_payment", label: "Down payment (GHS)", type: "text" },
      { name: "tenure_months", label: "Preferred tenure (months)", type: "number" },
    ],
  },
  fleet: {
    endpoint: "/fleet-enquiries",
    overline: "Fleet Sales",
    heading: ["Five vehicles.", "One <em>partner</em>."],
    intro: "Corporate orders of five vehicles or more receive dedicated pricing, delivery scheduling and after-sales support.",
    extra: [
      { name: "company", label: "Company", type: "text", required: true },
      { name: "fleet_size", label: "Fleet size", type: "number", required: true, placeholder: "e.g. 10" },
      { name: "industry", label: "Industry", type: "text" },
    ],
  },
  import: {
    endpoint: "/import-requests",
    overline: "Import on Request",
    heading: ["Name the car.", "We'll <em>find it</em>."],
    intro: "Not on the site? Tell us the make, model and year — we source, import and deliver anywhere in Ghana.",
    extra: [
      { name: "make", label: "Make", type: "text", required: true, placeholder: "e.g. Zeekr" },
      { name: "model", label: "Model", type: "text", required: true, placeholder: "e.g. 001" },
      { name: "year", label: "Year", type: "number" },
      { name: "notes", label: "Notes", type: "textarea" },
    ],
  },
  contact: {
    endpoint: "/contact-messages",
    overline: "Contact",
    heading: ["Say <em>hello</em>."],
    intro: "For general enquiries, partnerships or media requests.",
    extra: [{ name: "subject", label: "Subject", type: "text" }],
  },
};

export default function RequestForm({ kind }) {
  const cfg = CONFIG[kind];
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Prefill vehicle_slug from ?vehicle=
    const v = params.get("vehicle");
    if (v && cfg.extra.some((e) => e.name === "vehicle_slug")) {
      setForm((f) => ({ ...f, vehicle_slug: v }));
    }
  }, [params, cfg]);

  const onChange = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Coerce numeric & boolean fields
      const payload = { ...form };
      cfg.extra.forEach((ex) => {
        if (ex.type === "number" && payload[ex.name] !== undefined && payload[ex.name] !== "") {
          payload[ex.name] = Number(payload[ex.name]);
        }
        if (ex.type === "checkbox") payload[ex.name] = !!payload[ex.name];
      });
      await api.post(cfg.endpoint, payload);
      setDone(true);
      toast.success("Sent. We'll be in touch shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`form-page-${kind}`} className="pt-40 pb-32">
      <div className="mx-auto max-w-[1200px] px-6 md:px-10">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-6">{cfg.overline}</div>
            <RevealOnScroll
              element="h1"
              className="h-editorial text-[clamp(2.6rem,6vw,5rem)]"
              lines={cfg.heading}
            />
            <p className="mt-8 text-white/70 text-lg font-light leading-relaxed">{cfg.intro}</p>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                data-testid="form-success"
                className="border border-[#C5A059]/40 bg-[#C5A059]/5 p-10"
              >
                <div className="overline mb-3">Received</div>
                <h3 className="font-display text-3xl mb-3">Thank you.</h3>
                <p className="text-white/70">Your enquiry has been recorded. A member of the Maklan Motors team will be in touch shortly.</p>
              </motion.div>
            ) : (
              <form onSubmit={submit} data-testid="request-form" className="space-y-8">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="field-label">Full Name *</label>
                    <input required data-testid="form-input-name" className="field" value={form.name} onChange={(e) => onChange("name", e.target.value)} />
                  </div>
                  <div>
                    <label className="field-label">Email *</label>
                    <input required type="email" data-testid="form-input-email" className="field" value={form.email} onChange={(e) => onChange("email", e.target.value)} />
                  </div>
                  <div>
                    <label className="field-label">Phone *</label>
                    <input required data-testid="form-input-phone" className="field" value={form.phone} onChange={(e) => onChange("phone", e.target.value)} />
                  </div>
                  {cfg.extra.filter((f) => f.type !== "textarea" && f.type !== "checkbox").map((f) => (
                    <div key={f.name}>
                      <label className="field-label">{f.label}{f.required ? " *" : ""}</label>
                      <input
                        required={f.required}
                        type={f.type}
                        placeholder={f.placeholder}
                        data-testid={`form-input-${f.name}`}
                        className="field"
                        value={form[f.name] || ""}
                        onChange={(e) => onChange(f.name, e.target.value)}
                      />
                    </div>
                  ))}
                </div>

                {cfg.extra.filter((f) => f.type === "textarea").map((f) => (
                  <div key={f.name}>
                    <label className="field-label">{f.label}</label>
                    <textarea
                      data-testid={`form-input-${f.name}`}
                      className="field"
                      value={form[f.name] || ""}
                      onChange={(e) => onChange(f.name, e.target.value)}
                    />
                  </div>
                ))}

                <div>
                  <label className="field-label">Message</label>
                  <textarea data-testid="form-input-message" className="field" value={form.message} onChange={(e) => onChange("message", e.target.value)} />
                </div>

                {cfg.extra.filter((f) => f.type === "checkbox").map((f) => (
                  <label key={f.name} className="flex items-center gap-3 text-white/70 cursor-pointer">
                    <input type="checkbox" data-testid={`form-input-${f.name}`}
                      checked={!!form[f.name]} onChange={(e) => onChange(f.name, e.target.checked)}
                      className="w-4 h-4 accent-[#C5A059]" />
                    <span>{f.label}</span>
                  </label>
                ))}

                <button type="submit" data-testid="form-submit" disabled={loading} className="btn-gold disabled:opacity-60">
                  {loading ? "Sending…" : "Submit Enquiry"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
